import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, Subject, throwError } from 'rxjs';

import {
  IrpfAscendantDto,
  IrpfDescendantDto,
  IrpfIndividualDto,
  IrpfPensionPaymentDto
} from '@app/data/dtos/ptrab/irpf/irpf.dto';
import {
  mapAscendantsDto,
  mapDescendantsDto,
  mapPensionPaymentDto,
  mapWorkerDto
} from '@app/data/mappers/ptrab/irpf/irpf.mappers';
import { IRPF_REPOSITORY, IrpfRepository } from '@app/domain/repositories/ptrab/irpf/irpf.repository.contract';
import { ErrorMsgEnum } from '@app/entities/enums/error/error-msg-enum';
import { IrpfSections } from '@app/entities/enums/ptrab/irpf.enum';
import { TwoFactorAuthorization } from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { ErrorCodes } from '@app/entities/models/error/error.model';
import { Ancestor, Descendant, IrpfModel, PensionPayment, Worker } from '@app/entities/models/ptrab/irpf/irpf.model';

import { IrpfUseCases } from './irpf.use-cases.contract';

@Injectable({
  providedIn: 'root'
})
export class IrpfUseCasesImpl implements IrpfUseCases {
  #errorSubject = new Subject<ErrorMsgEnum>();
  error$ = this.#errorSubject.asObservable();
  #irpfRepository: IrpfRepository = inject(IRPF_REPOSITORY);

  getData(): Observable<IrpfModel> {
    return this.#irpfRepository.getData().pipe(catchError(this.#handleError));
  }

  modifyIrpfData(
    data: boolean | Worker | Descendant[] | Ancestor[] | PensionPayment,
    section: IrpfSections,
    authorization?: TwoFactorAuthorization
  ): Observable<IrpfModel> {
    const lowercaseSection = section.toLowerCase();

    const value = this.getDataFromSection(section, data);
    if (value === undefined) {
      throw new Error('Sección IRPF no válida o datos insuficientes para la sección.');
    }

    return this.#irpfRepository
      .modifyIrpfData(
        {
          data: {
            [lowercaseSection]: value
          }
        },
        section,
        authorization
      )
      .pipe(catchError(this.#handleError));
  }

  getDataFromSection(
    irpfSection: IrpfSections,
    data: boolean | Worker | Descendant[] | Ancestor[] | PensionPayment
  ): boolean | IrpfIndividualDto | IrpfDescendantDto[] | IrpfAscendantDto[] | IrpfPensionPaymentDto {
    switch (irpfSection) {
      case IrpfSections.INDIVIDUAL:
        return mapWorkerDto(data as Worker);
      case IrpfSections.DESCENDANTS:
        return mapDescendantsDto(data as Descendant[]);
      case IrpfSections.ASCENDANTS:
        return mapAscendantsDto(data as Ancestor[]);
      case IrpfSections.SUPPORT_PAYMENT:
        return mapPensionPaymentDto(data as PensionPayment);
      case IrpfSections.MORTGAGE_REDUCTION:
        return data as boolean;
      case IrpfSections.CEUTA_MELILLA_RESIDENCE:
        return data as boolean;
      default:
        throw new Error('Sección IRPF no válida o datos insuficientes para la sección.');
    }
  }

  #handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorType;

    switch (error.status) {
      case ErrorCodes.LOCKED:
        errorType = ErrorMsgEnum.SERVICE_LOCKED;
        break;
      case ErrorCodes.SECTION_MAINTENANCE:
        errorType = ErrorMsgEnum.SECTION_MAINTENANCE;
        break;
      default:
        errorType = ErrorMsgEnum.SERVER_ERROR;
        break;
    }

    this.#errorSubject.next(errorType);

    return throwError(() => error);
  };
}
